import { getAttribute } from '@/presenters/components/search/Filters/utils/getAttribute';

import { SOLD_BY_ATTRIBUTE, FILTERS_MAP } from '../../constants';
import { PRICE_URL_TEXT } from '../filters/price-range/price-range';
import {
  safeDecodeURI,
  safeDecodeURIComponent,
} from '../utils/safe-decode-uri';

type RefinementList = {
  [key: string]: Array<string>;
};

type ToggleProps = {
  [key: string]: boolean;
};

export type RouteStateAttributesProps = {
  refinementList?: RefinementList;
  toggle?: ToggleProps;
  page?: number | string;
  sortBy?: string;
  range?: Record<string, string>;
};

interface RebuildUrlProps {
  routeState: RouteStateAttributesProps;
  urlToRebuild: string;
  originalPath: string;
  region: string;
}

export const rebuildUrl = (props: RebuildUrlProps) => {
  const { originalPath, region, routeState, urlToRebuild } = props;

  const decodedUrl = safeDecodeURIComponent(urlToRebuild);
  let filtersInUrl = getFiltersUrl(decodedUrl);

  if (!filtersInUrl || Object.keys(routeState).length === 0) {
    return originalPath;
  }

  const filtersToRemove = getFiltersToRemove(filtersInUrl, routeState, region);
  if (!filtersToRemove.length) return urlToRebuild;

  const filtersToRemoveSet = new Set(filtersToRemove);
  const updatedFilters = filtersInUrl.filter((f) => !filtersToRemoveSet.has(f));

  return reassembleUrl(originalPath, updatedFilters, region);
};

const reassembleUrl = (
  basePath: string,
  filters: string[],
  region: string
): string => {
  let newUrl = basePath;

  filters.forEach((filter, index) => {
    const toggles = getAttribute(region).toggle;

    const specialToggleKeys = [
      `regionalAttributes.${region}.offer.enabled`,
      `regionalAttributes.${region}.facets.freeShipping`,
      `regionalAttributes.${region}.facets.blackFriday`,
    ];

    for (const [key, toggle] of Object.entries(toggles)) {
      if (
        specialToggleKeys.includes(key) &&
        filter.includes(String(toggle.filterValue)) &&
        index === 0
      ) {
        filter = toggle.filterName;
        break;
      }
    }

    const isQueryParam = filter.includes('filter') || filter.includes('order');

    const [key, value] = isQueryParam ? filter.split('=') : [];
    const orderSuffix = filter.includes('order') ? '' : '/';

    const formattedFilter = isQueryParam
      ? `${key
          .replace(/filter\[/g, '')
          .replace(/\]/g, '')
          .replace('order', '')}${orderSuffix}${value}`
      : filter.replace('=', '/');

    if (index === 0) {
      newUrl += '/' + formattedFilter;
    } else {
      newUrl += (index === 1 ? '?' : '&') + filter;
    }
  });

  return newUrl;
};

export const getFiltersUrl = (url: string): string[] | undefined => {
  const { pathname, search } = new URL(url);

  const segments = safeDecodeURI(pathname).split('/');
  const isEntryKey = segments[1] === 'entry-key';

  const skip = isEntryKey ? 3 : 2;
  const firstSegment = segments.slice(skip).join('/');

  // IMPORTANT: This regex is necessary to work on Safari IOS version <= 18
  const otherSegments = safeDecodeURI(search)
    .replace(/_&/g, '_@and@_')
    .split(/[?&]/)
    .filter(Boolean)
    .map((str) => str.replace(/_@and@_/g, '_&'));

  return [firstSegment, ...otherSegments].filter(Boolean);
};

const getFiltersToRemove = (
  filters: string[],
  routeState: RouteStateAttributesProps,
  region: string
): string[] => {
  return filters.filter((filter) => {
    return !(
      checkRefinementList(filter, routeState.refinementList) ||
      checkPage(filter, routeState.page) ||
      checkSort(filter, routeState.sortBy) ||
      checkToggle(filter, routeState.toggle, region) ||
      checkRange(filter, routeState.range)
    );
  });
};

const checkRefinementList = (
  filter: string,
  refinementList?: RefinementList
): boolean => {
  if (!refinementList) return false;

  for (const [key, values] of Object.entries(refinementList)) {
    if (key === SOLD_BY_ATTRIBUTE && filter.includes('vendido-por')) {
      return values.some((val) =>
        filter.toLowerCase().includes(val.replaceAll(' ', '_').toLowerCase())
      );
    }

    if (
      (key.includes('attributes') || FILTERS_MAP[key]) &&
      values.some((val) =>
        filter.toLowerCase().includes(val.replaceAll(' ', '_').toLowerCase())
      )
    ) {
      return true;
    }
  }

  return false;
};

const checkPage = (filter: string, page?: number | string): boolean => {
  return page ? filter === `page=${page}` : false;
};

const checkSort = (filter: string, sortBy?: string): boolean => {
  return sortBy ? /melhor-preco|menor-preco/.test(filter) : false;
};

const checkToggle = (
  filter: string,
  toggle?: ToggleProps,
  region?: string
): boolean => {
  if (!toggle || !region) return false;

  const toggles = getAttribute(region).toggle;

  return Object.keys(toggle).some((key) =>
    filter.includes(toggles[key]?.filterName)
  );
};

const checkRange = (
  filter: string,
  range?: Record<string, string>
): boolean => {
  return range ? filter.includes(PRICE_URL_TEXT) : false;
};
